/**
 * Lv6 AI Worker の生成抽象化
 *
 * Worker 利用環境（ブラウザ）と非利用環境（jest test 環境等）で挙動を切り替える。
 *
 * - ブラウザ: Vite が `new URL` 構文を Worker バンドルに解決
 * - jest（jsdom 含まず）: Worker グローバルが存在しない → null を返す
 *
 * 利用側は null チェックでフォールバック（インライン実行モード）に切り替える。
 */

/**
 * Worker のように使える最小インターフェース。
 * テスト時にスタブと差し替えられるよう、Worker 全体ではなく
 * 必要最小限の API のみを定義。
 */
export interface WorkerLike {
  postMessage(message: unknown): void;
  addEventListener(
    type: 'message' | 'error' | 'messageerror',
    listener: (event: MessageEvent | ErrorEvent) => void,
  ): void;
  removeEventListener(
    type: 'message' | 'error' | 'messageerror',
    listener: (event: MessageEvent | ErrorEvent) => void,
  ): void;
  terminate(): void;
}

let cachedWorker: WorkerLike | null = null;
let workerCreationFailed = false;

/**
 * Lv6 用 Worker のシングルトンを取得する
 *
 * 初回呼び出し時に lazy 生成。失敗した場合は null を返し、それ以降は
 * 試行しない（無駄なリトライを防ぐ）。
 *
 * @returns Worker（生成成功時）または null（Worker非対応環境/生成失敗時）
 */
export function getOrCreateAIWorker(): WorkerLike | null {
  if (cachedWorker) return cachedWorker;
  if (workerCreationFailed) return null;
  if (typeof Worker === 'undefined') {
    workerCreationFailed = true;
    return null;
  }

  try {
    // Vite は new URL(...) 構文を Worker エントリとして解決する。
    // import.meta.url は ESM/ブラウザでのみ有効。jest（CommonJS）では
    // typeof Worker === 'undefined' で先に early return されるためここには到達しない。
    // @ts-ignore import.meta is only available in ESM/Vite build, not in jest CommonJS
    const workerUrl = new URL('./ai.worker.ts', import.meta.url);
    const worker = new Worker(workerUrl, {
      type: 'module',
      name: 'ai-worker',
    });
    cachedWorker = worker;
    return worker;
  } catch {
    workerCreationFailed = true;
    return null;
  }
}

/**
 * キャッシュ済み Worker を破棄する（テスト用 or エラー時の再生成用）
 */
export function disposeAIWorker(): void {
  if (cachedWorker) {
    try {
      cachedWorker.terminate();
    } catch {
      // ignore
    }
    cachedWorker = null;
  }
  workerCreationFailed = false;
}

/**
 * テスト用に Worker をスタブと差し替える
 *
 * @param stub テスト用 Worker、null で実 Worker に戻す（次回 getOrCreate で再生成）
 */
export function setAIWorkerForTesting(stub: WorkerLike | null): void {
  cachedWorker = stub;
  workerCreationFailed = false;
}
